import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthResponse, CryptoBundle, LoginChallengeResponse, UserDto } from './models';
import { CryptoService } from './crypto.service';

const RT_KEY = 'pk_rt';
const DT_KEY = 'pk_dt';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly cryptoService = inject(CryptoService);
  private readonly base = `${environment.apiUrl}/auth`;

  private accessToken: string | null = null;
  private cryptoBundle: CryptoBundle | null = null;

  readonly user = signal<UserDto | null>(null);
  readonly isAuthenticated = computed(() => this.user() !== null);

  getAccessToken(): string | null {
    return this.accessToken;
  }

  /** Merge profile edits into the cached user so the UI (e.g. navbar) updates immediately. */
  applyUserUpdate(patch: Partial<UserDto>): void {
    this.user.update((u) => (u ? { ...u, ...patch } : u));
  }

  /** True while a refresh token is stored — the session may still be recoverable. */
  hasStoredSession(): boolean {
    return localStorage.getItem(RT_KEY) !== null;
  }

  getCryptoBundle(): CryptoBundle | null {
    return this.cryptoBundle;
  }

  /** Refresh the cached bundle after a master password change. */
  applyCryptoUpdate(bundle: CryptoBundle): void {
    this.cryptoBundle = bundle;
  }

  private get deviceToken(): string | null {
    return localStorage.getItem(DT_KEY);
  }

  register(payload: {
    email: string;
    firstName: string;
    lastName: string;
    loginPassword: string;
    masterPasswordHint?: string;
    crypto: CryptoBundle;
  }): Promise<{ message: string }> {
    return firstValueFrom(this.http.post<{ message: string }>(`${this.base}/register`, payload));
  }

  confirmEmail(email: string, code: string): Promise<{ message: string }> {
    return firstValueFrom(
      this.http.post<{ message: string }>(`${this.base}/confirm-email`, { email, code })
    );
  }

  /** `identifier` is the account email or @username. */
  resendOtp(identifier: string, purpose: string): Promise<unknown> {
    return firstValueFrom(this.http.post(`${this.base}/resend-otp`, { identifier, purpose }));
  }

  /**
   * Step 1 of login; `identifier` is the account email or @username.
   * Returns a challenge (OTP required) or a full auth response (trusted device).
   */
  async login(
    identifier: string,
    loginPassword: string
  ): Promise<{ challenge?: LoginChallengeResponse; authenticated: boolean }> {
    const res = await firstValueFrom(
      this.http.post<AuthResponse | LoginChallengeResponse>(`${this.base}/login`, {
        identifier,
        loginPassword,
        deviceToken: this.deviceToken
      })
    );

    if ((res as LoginChallengeResponse).otpRequired) {
      return { challenge: res as LoginChallengeResponse, authenticated: false };
    }
    this.applyAuth(res as AuthResponse);
    return { authenticated: true };
  }

  /** Step 2 of login: verify the OTP code. */
  async verifyLogin(identifier: string, code: string, rememberDevice: boolean): Promise<void> {
    const res = await firstValueFrom(
      this.http.post<AuthResponse>(`${this.base}/login/verify`, { identifier, code, rememberDevice })
    );
    this.applyAuth(res);
  }

  forgotPassword(email: string): Promise<unknown> {
    return firstValueFrom(this.http.post(`${this.base}/forgot-password`, { email }));
  }

  resetPassword(email: string, code: string, newLoginPassword: string): Promise<unknown> {
    return firstValueFrom(
      this.http.post(`${this.base}/reset-password`, { email, code, newLoginPassword })
    );
  }

  private restorePromise: Promise<boolean> | null = null;

  /**
   * Attempt to restore a session on app start using the stored refresh token.
   * Single-flight: the server rotates refresh tokens (revoke-on-use), so
   * concurrent callers (e.g. route guards running in parallel) must share one
   * request — a second POST with the same token would 401 and wipe the session.
   */
  tryRestore(): Promise<boolean> {
    this.restorePromise ??= this.doRestore().finally(() => (this.restorePromise = null));
    return this.restorePromise;
  }

  private async doRestore(): Promise<boolean> {
    const rt = localStorage.getItem(RT_KEY);
    if (!rt) return false;
    try {
      const res = await firstValueFrom(
        this.http.post<AuthResponse>(`${this.base}/refresh`, { refreshToken: rt })
      );
      this.applyAuth(res);
      return true;
    } catch (e) {
      // Only a definitive rejection means the token is dead. Keep it on transient
      // failures (rate limit, network, server error) so a later attempt can restore.
      if (e instanceof HttpErrorResponse && (e.status === 400 || e.status === 401)) {
        this.clear();
      }
      return false;
    }
  }

  async refresh(): Promise<boolean> {
    return this.tryRestore();
  }

  async logout(): Promise<void> {
    const rt = localStorage.getItem(RT_KEY);
    if (rt) {
      try {
        await firstValueFrom(this.http.post(`${this.base}/logout`, { refreshToken: rt }));
      } catch {
        /* ignore */
      }
    }
    this.clear();
    this.router.navigate(['/login']);
  }

  private applyAuth(res: AuthResponse): void {
    this.accessToken = res.accessToken;
    this.cryptoBundle = res.crypto;
    this.user.set(res.user);
    localStorage.setItem(RT_KEY, res.refreshToken);
    if (res.deviceToken) {
      localStorage.setItem(DT_KEY, res.deviceToken);
    }
  }

  private clear(): void {
    this.accessToken = null;
    this.cryptoBundle = null;
    this.user.set(null);
    this.cryptoService.lock();
    localStorage.removeItem(RT_KEY);
  }
}
